/**
 * Format Code for the Playground editor: `PG-IDE02`, `docs/24` §10.2.
 *
 * One rule decides the whole design: a formatter is only offered where it is
 * reliable and runs in the browser without sending source anywhere. Three
 * WebAssembly builds of real formatters are lazy-loaded on the first click
 * (clang-format for C, C++ and Java; ruff for Python; gofmt for Go), JavaScript
 * and TypeScript use the formatter Monaco's own TypeScript service already
 * ships, and the rest of the catalogue (Rust, PHP, Ruby) is stated as not
 * available rather than pretended. No remote formatting service, no binary
 * execution outside the sandboxed wasm, no all-language dependency.
 *
 * Source never leaves the page: the wasm modules are same-origin static assets
 * emitted by the bundler, and formatting is a pure function over the string.
 *
 * Keyed by Monaco language id, because that is what the editor already holds
 * and what the toolbar can ask about before anything is loaded.
 */

export type FormatSupport = "monaco" | "wasm" | "unsupported";

type WasmFamily = "clang" | "ruff" | "gofmt";

export interface FormatOptions {
  /** The editor's indent width; every formatter that takes one honours it. */
  tabSize: number;
}

export type FormatFailureReason = "syntax" | "load" | "unknown";

export class FormatError extends Error {
  constructor(
    readonly reason: FormatFailureReason,
    message: string,
  ) {
    super(message);
    this.name = "FormatError";
  }
}

interface FormatterEntry {
  family: WasmFamily;
  /** The filename the formatter is told, which is how clang-format picks a language. */
  filename: string;
}

const WASM_FORMATTERS: Readonly<Record<string, FormatterEntry>> = Object.freeze({
  c: { family: "clang", filename: "main.c" },
  cpp: { family: "clang", filename: "main.cpp" },
  java: { family: "clang", filename: "Main.java" },
  python: { family: "ruff", filename: "main.py" },
  go: { family: "gofmt", filename: "main.go" },
});

const MONACO_FORMATTERS: ReadonlySet<string> = new Set(["javascript", "typescript"]);

export function formatSupportFor(monacoLanguage: string): FormatSupport {
  if (MONACO_FORMATTERS.has(monacoLanguage)) return "monaco";
  if (monacoLanguage in WASM_FORMATTERS) return "wasm";
  return "unsupported";
}

/**
 * The smallest single edit that turns `original` into `formatted`.
 *
 * A formatter answers with the whole document, and replacing the whole document
 * is one edit that moves every cursor to the end. Trimming the common prefix
 * and suffix first leaves one edit over the region that actually changed, so a
 * cursor outside it stays put and the edit is still one undo step.
 */
export function minimalEdit(
  original: string,
  formatted: string,
): { start: number; end: number; text: string } | null {
  if (original === formatted) return null;
  let start = 0;
  const maxPrefix = Math.min(original.length, formatted.length);
  while (start < maxPrefix && original.charCodeAt(start) === formatted.charCodeAt(start)) {
    start += 1;
  }
  let endOriginal = original.length;
  let endFormatted = formatted.length;
  while (
    endOriginal > start &&
    endFormatted > start &&
    original.charCodeAt(endOriginal - 1) === formatted.charCodeAt(endFormatted - 1)
  ) {
    endOriginal -= 1;
    endFormatted -= 1;
  }
  return { start, end: endOriginal, text: formatted.slice(start, endFormatted) };
}

type WasmFormatFn = (source: string, filename: string, options: FormatOptions) => string;

/**
 * Where each wasm binary comes from. The relative paths reach into
 * `node_modules` on purpose: webpack resolves a relative `new URL(...,
 * import.meta.url)` into a hashed static asset, which is the only way the
 * binary becomes a same-origin file without a copy step in the build. A bare
 * package specifier inside `new URL` is not something every bundler resolves,
 * and a request it does not resolve fails silently in the browser.
 *
 * Tests inject bytes instead, through {@link __setWasmSourceForTests}.
 */
type WasmSource = (family: WasmFamily) => URL | BufferSource;

function defaultWasmSource(family: WasmFamily): URL {
  switch (family) {
    case "clang":
      return new URL(
        "../../../node_modules/@wasm-fmt/clang-format/clang-format.wasm",
        import.meta.url,
      );
    case "ruff":
      return new URL("../../../node_modules/@wasm-fmt/ruff_fmt/ruff_fmt_bg.wasm", import.meta.url);
    case "gofmt":
      return new URL("../../../node_modules/@wasm-fmt/gofmt/gofmt.wasm", import.meta.url);
  }
}

let wasmSource: WasmSource = defaultWasmSource;

/** @internal Test seam: hand the loaders bytes read from disk instead of a URL. */
export function __setWasmSourceForTests(source: WasmSource | null): void {
  wasmSource = source ?? defaultWasmSource;
  loaded.clear();
}

const loaded = new Map<WasmFamily, Promise<WasmFormatFn>>();

async function loadFamily(family: WasmFamily): Promise<WasmFormatFn> {
  switch (family) {
    case "clang": {
      const mod = await import("@wasm-fmt/clang-format/web");
      await mod.default(wasmSource(family));
      return (source, filename, options) =>
        mod.format(
          source,
          filename,
          JSON.stringify({
            BasedOnStyle: "Google",
            IndentWidth: options.tabSize,
            TabWidth: options.tabSize,
            ColumnLimit: 100,
            AllowShortFunctionsOnASingleLine: "Empty",
            AllowShortIfStatementsOnASingleLine: "Never",
            AllowShortLoopsOnASingleLine: false,
          }),
        );
    }
    case "ruff": {
      const mod = await import("@wasm-fmt/ruff_fmt/web");
      await mod.default(wasmSource(family));
      return (source, filename, options) =>
        mod.format(source, filename, {
          indent_style: "space",
          indent_width: options.tabSize,
          line_width: 88,
        });
    }
    case "gofmt": {
      const mod = await import("@wasm-fmt/gofmt/web");
      await mod.default(wasmSource(family));
      return (source) => mod.format(source);
    }
  }
}

function getFormatter(family: WasmFamily): Promise<WasmFormatFn> {
  let pending = loaded.get(family);
  if (!pending) {
    pending = loadFamily(family).catch((error: unknown) => {
      // A failed load is retried on the next click rather than cached as a
      // permanent refusal: the usual cause is a dropped connection mid-download.
      loaded.delete(family);
      throw new FormatError("load", error instanceof Error ? error.message : String(error));
    });
    loaded.set(family, pending);
  }
  return pending;
}

/**
 * Formats `source` for a wasm-backed language. Callers check
 * {@link formatSupportFor} first; `monaco` languages are formatted by the
 * editor itself and `unsupported` ones never reach here.
 */
export async function formatSourceWithWasm(
  monacoLanguage: string,
  source: string,
  options: FormatOptions,
): Promise<string> {
  const entry = WASM_FORMATTERS[monacoLanguage];
  if (!entry) {
    throw new FormatError("unknown", `No wasm formatter for ${monacoLanguage}`);
  }
  const format = await getFormatter(entry.family);
  try {
    return format(source, entry.filename, options);
  } catch (error) {
    // ruff and gofmt refuse a file that does not parse; clang-format formats
    // what it can. A refusal is the student's syntax error, and the message
    // says so instead of blaming the tool.
    throw new FormatError("syntax", error instanceof Error ? error.message : String(error));
  }
}
